// Ban log client. `apps.is_banned` stays the current state; these rows are the
// history behind it — who marked the app, what happened, and the screenshots.
import { api } from './client';

export interface AppBanScreenshot {
  id: string;
  ban_id: string;
  content_type: string;
  size_bytes: number;
  filename: string | null;
  created_at: string;
  uploaded_by_name: string | null;
  uploaded_by_email: string | null;
}

export interface AppBan {
  id: string;
  app_id: string;
  source: 'manual' | 'auto';
  reason: string;
  detected_at: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
  cleared_at: string | null;
  cleared_by_name: string | null;
  cleared_by_email: string | null;
  cleared_note: string | null;
  app_name: string | null;
  bundle_id: string | null;
  platform: 'ios' | 'android';
  icon_url: string | null;
  is_banned: number;
  screenshot_count: number;
  screenshots: AppBanScreenshot[];
}

export interface AppBanSummary {
  total: number;
  open: number;
  cleared: number;
  manual: number;
  apps: number;
}

const BASE = '/admin/apps/bans';

export type AppBanState = 'all' | 'open' | 'cleared';

export const getAppBans = (params: { app_id?: string; state?: AppBanState; limit?: number } = {}) => {
  const q = new URLSearchParams();
  if (params.app_id) q.set('app_id', params.app_id);
  if (params.state && params.state !== 'all') q.set('state', params.state);
  if (params.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return api.get<{ bans: AppBan[]; summary: AppBanSummary }>(`${BASE}${qs ? `?${qs}` : ''}`);
};

export const createAppBan = (input: { app_id: string; reason: string; detected_at?: string }) =>
  api.post<AppBan>(BASE, input);

export const updateAppBan = (id: string, patch: { reason?: string; detected_at?: string }) =>
  api.patch<AppBan>(`${BASE}/${encodeURIComponent(id)}`, patch);

export const clearAppBan = (id: string, note?: string) =>
  api.post<AppBan>(`${BASE}/${encodeURIComponent(id)}/clear`, { note });

/** Raw bytes + Content-Type, like the other uploads here. Returns the ban with
 *  its screenshot list refreshed. */
export async function uploadAppBanScreenshot(banId: string, file: File): Promise<AppBan> {
  const bytes = await file.arrayBuffer();
  const qs = file.name ? `?filename=${encodeURIComponent(file.name)}` : '';
  return api.upload<AppBan>(
    `${BASE}/${encodeURIComponent(banId)}/screenshots${qs}`,
    bytes,
    file.type || 'application/octet-stream',
  );
}

export const deleteAppBanScreenshot = (shotId: string) =>
  api.delete<AppBan>(`${BASE}/screenshots/${encodeURIComponent(shotId)}`);

/** No public link — the bytes come through the authorized client. */
export const getAppBanScreenshotImage = (shotId: string) =>
  api.blob(`${BASE}/screenshots/${encodeURIComponent(shotId)}/image`);
